<template>
  <div class="email-template-editor">
    <!-- Email type tabs -->
    <div class="email-type-tabs">
      <button
        v-for="type in emailTypes"
        :key="type.key"
        class="email-type-tab"
        :class="{ active: selectedEmailType === type.key }"
        @click="selectEmailType(type.key)"
      >
        {{ type.label }}
      </button>
    </div>

    <div class="editor-layout">
      <!-- Editor panel -->
      <div class="editor-panel">
        <div class="panel-header">
          <span class="panel-title">Editor</span>
          <span v-if="!clienteId" class="status-badge global">
            🌐 Plantilla Global
          </span>
          <span v-else-if="!isDefault" class="status-badge custom">
            <i class="material-icons">check_circle</i>
            Personalizado
          </span>
          <span v-else class="status-badge default">
            <i class="material-icons">info</i>
            Por defecto
          </span>
        </div>

        <div class="panel-body">
          <!-- Subject -->
          <div class="form-group">
            <label class="form-label">Asunto</label>
            <input
              type="text"
              class="input"
              v-model="template.subject"
              placeholder="Asunto del correo"
              @input="updatePreview"
            />
          </div>

          <!-- Greeting editor -->
          <div class="form-group">
            <label class="form-label">Saludo</label>
            <div class="greeting-editor-container">
              <div class="greeting-editor-row">
                <div
                  ref="greetingEditor"
                  class="greeting-editor"
                  contenteditable="true"
                  data-placeholder="Escribe el saludo..."
                  @input="onGreetingInput"
                  @keydown="onGreetingKeydown"
                ></div>
                <button
                  type="button"
                  class="greeting-insert-btn"
                  :disabled="greetingHasNombreChip"
                  @click="insertGreetingChip"
                  title="Insertar nombre del destinatario"
                >
                  <i class="material-icons">add</i>
                  Nombre
                </button>
              </div>
            </div>
          </div>

          <!-- Body editor -->
          <div class="form-group">
            <label class="form-label">Contenido del correo</label>
            <div class="rich-editor-container">
              <div class="rich-editor-toolbar">
                <div class="toolbar-group">
                  <button
                    class="toolbar-btn"
                    title="Negrita"
                    @click="formatText('bold')"
                  >
                    <i class="material-icons">format_bold</i>
                  </button>
                  <button
                    class="toolbar-btn"
                    title="Cursiva"
                    @click="formatText('italic')"
                  >
                    <i class="material-icons">format_italic</i>
                  </button>
                  <button
                    class="toolbar-btn"
                    title="Enlace"
                    @click="insertLink"
                  >
                    <i class="material-icons">link</i>
                  </button>
                </div>
                <div class="toolbar-divider"></div>
                <div class="toolbar-group">
                  <button
                    class="toolbar-btn"
                    title="Alinear izquierda"
                    @click="formatText('justifyLeft')"
                  >
                    <i class="material-icons">format_align_left</i>
                  </button>
                  <button
                    class="toolbar-btn"
                    title="Centrar"
                    @click="formatText('justifyCenter')"
                  >
                    <i class="material-icons">format_align_center</i>
                  </button>
                  <button
                    class="toolbar-btn"
                    title="Alinear derecha"
                    @click="formatText('justifyRight')"
                  >
                    <i class="material-icons">format_align_right</i>
                  </button>
                </div>
                <div class="toolbar-divider"></div>
                <div class="toolbar-group">
                  <button
                    class="toolbar-insert"
                    @click="insertPlaceholder('nombre')"
                  >
                    <span class="chip-preview nombre">
                      <i class="material-icons">account_circle</i>
                      Nombre
                    </span>
                  </button>
                  <button
                    class="toolbar-insert"
                    @click="insertPlaceholder('empresa')"
                  >
                    <span class="chip-preview empresa">
                      <i class="material-icons">business</i>
                      Empresa
                    </span>
                  </button>
                </div>
              </div>
              <div
                class="rich-editor-content"
                contenteditable="true"
                ref="editor"
                @input="onEditorInput"
                @paste="onPaste"
                @click="onEditorClick"
                data-placeholder="Escribe el contenido del correo..."
              ></div>
              <!-- Link tooltip (crear/editar) -->
              <div
                v-if="showLinkTooltip"
                class="link-tooltip"
                :style="{
                  top: tooltipPosition.top + 'px',
                  left: tooltipPosition.left + 'px',
                }"
              >
                <div class="link-tooltip-arrow"></div>
                <input
                  ref="linkInput"
                  v-model="linkUrl"
                  type="text"
                  placeholder="https://..."
                  class="link-tooltip-input"
                  @keyup.enter="applyLink"
                  @keyup.esc="cancelLink"
                />
                <div class="link-tooltip-actions">
                  <button class="link-tooltip-btn cancel" @click="cancelLink">
                    <i class="material-icons">close</i>
                  </button>
                  <button class="link-tooltip-btn apply" @click="applyLink">
                    <i class="material-icons">check</i>
                  </button>
                </div>
              </div>
              <!-- Link preview popover -->
              <div
                v-if="showLinkPreview"
                class="link-preview-popover"
                :style="{
                  top: tooltipPosition.top + 'px',
                  left: tooltipPosition.left + 'px',
                }"
              >
                <div class="link-tooltip-arrow"></div>
                <a
                  :href="activeLinkUrl"
                  target="_blank"
                  class="link-preview-url"
                  >{{ truncatedLinkUrl }}</a
                >
                <div class="link-preview-actions">
                  <button
                    class="link-tooltip-btn edit"
                    @click="editActiveLink"
                    title="Editar"
                  >
                    <i class="material-icons">edit</i>
                  </button>
                  <button
                    class="link-tooltip-btn remove"
                    @click="removeActiveLink"
                    title="Eliminar"
                  >
                    <i class="material-icons">link_off</i>
                  </button>
                </div>
              </div>
            </div>
          </div>

          <!-- CTA Section -->
          <div
            class="toggle-section"
            :class="{ open: template.cta.enabled || isFixedCta }"
          >
            <div class="toggle-header">
              <div class="toggle-header-left">
                <i class="material-icons">touch_app</i>
                <span>Botón de acción</span>
              </div>
              <div
                v-if="!isFixedCta"
                class="toggle-switch"
                :class="{ active: template.cta.enabled }"
                @click="toggleCta"
              ></div>
            </div>
            <div class="toggle-content" v-if="template.cta.enabled || isFixedCta">
              <div class="form-group">
                <label class="form-label">Texto del botón</label>
                <input
                  type="text"
                  class="input"
                  v-model="template.cta.text"
                  placeholder="Ej: Ir a la encuesta"
                  @input="updatePreview"
                />
              </div>
              <div class="form-group" v-if="!isFixedCta">
                <label class="form-label">URL del botón</label>
                <div class="radio-group">
                  <label
                    class="radio-item"
                    :class="{ selected: template.cta.urlType === 'auto' }"
                  >
                    <input
                      type="radio"
                      v-model="template.cta.urlType"
                      value="auto"
                    />
                    <span class="radio-circle"></span>
                    <span class="radio-label"
                      >Automática (link de encuesta)</span
                    >
                  </label>
                  <label
                    class="radio-item"
                    :class="{ selected: template.cta.urlType === 'custom' }"
                  >
                    <input
                      type="radio"
                      v-model="template.cta.urlType"
                      value="custom"
                    />
                    <span class="radio-circle"></span>
                    <span class="radio-label">Personalizada</span>
                  </label>
                </div>
                <input
                  v-if="template.cta.urlType === 'custom'"
                  type="text"
                  class="input"
                  v-model="template.cta.customUrl"
                  placeholder="https://..."
                  style="margin-top: 10px"
                />
              </div>
              <div class="form-group" v-else>
                <span class="status-badge default" style="white-space: normal">
                  <i class="material-icons">info</i>
                  El enlace de este botón se genera automáticamente y no se
                  puede modificar
                </span>
              </div>
            </div>
          </div>

          <!-- Footer note -->
          <div class="form-group">
            <label class="form-label">Nota al pie (opcional)</label>
            <input
              type="text"
              class="input"
              v-model="template.footerNote"
              placeholder="Ej: Este enlace es personal e intransferible"
              @input="updatePreview"
            />
          </div>

          <!-- Actions -->
          <div class="button-group">
            <button
              class="button is-dark"
              :class="{ 'is-loading': saving }"
              @click="saveTemplate"
            >
              Guardar
            </button>
            <button class="button" @click="resetToDefault">
              Restaurar predeterminado
            </button>
          </div>
        </div>
      </div>

      <!-- Preview panel -->
      <div class="preview-panel">
        <div class="panel-header">
          <span class="panel-title">Vista previa</span>
        </div>
        <div class="panel-body preview-body">
          <div class="email-preview-frame">
            <div class="email-preview">
              <div class="email-logo">SIETE</div>
              <div class="email-card">
                <div class="email-header">
                  <div class="email-icon">
                    <i class="material-icons">person</i>
                  </div>
                  <h1 v-if="hasSubjectContent" class="email-title">
                    {{ previewSubject }}
                  </h1>
                  <div v-else class="email-subject-placeholder">
                    <i class="material-icons">info</i>
                    <span>Asunto requerido</span>
                  </div>
                </div>
                <div class="email-body">
                  <div class="email-tag">{{ moduleTag }}</div>
                  <div class="email-greeting">{{ previewGreeting }}</div>
                  <div
                    v-if="hasBodyContent"
                    class="email-content"
                    v-html="previewBody"
                  ></div>
                  <div v-else class="email-content-placeholder">
                    <p>
                      <i class="material-icons">info</i>
                      Contenido requerido (mín. 50 caracteres)
                    </p>
                  </div>
                  <div
                    v-if="template.cta.enabled && template.cta.text"
                    class="email-cta"
                  >
                    <a href="#">{{ template.cta.text }}</a>
                  </div>
                  <div v-if="template.footerNote" class="email-footer-note">
                    {{ template.footerNote }}
                  </div>
                </div>
                <div class="email-footer">
                  Siete S.A.S. · {{ empresaNombre }}<br />
                  Si tienes dificultades, comunícate con
                  <a href="mailto:elena@sietegho.com">elena@sietegho.com</a>
                </div>
              </div>
              <!-- Redes sociales fuera de la tarjeta -->
              <div class="email-social-links">
                <a href="https://www.sietegho.com" target="_blank" class="social-btn web">
                  <img :src="require('@/assets/icons/earth.png')" alt="Web" />
                </a>
                <a href="https://www.linkedin.com/company/sietegho" target="_blank" class="social-btn linkedin">
                  <img :src="require('@/assets/icons/linkedin.png')" alt="LinkedIn" />
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Botón flotante preview móvil -->
    <button
      class="preview-fab"
      @click="showMobilePreview = true"
    >
      <i class="material-icons">visibility</i>
    </button>

    <!-- Overlay de preview móvil -->
    <transition name="preview-expand">
    <div v-if="showMobilePreview" class="mobile-preview-overlay">
      <div class="mobile-preview-header">
        <button class="back-btn" @click="showMobilePreview = false">
          <i class="material-icons">arrow_back</i>
          Volver
        </button>
        <button
          class="save-btn-mobile"
          :class="{ 'is-loading': saving }"
          @click="saveAndClosePreview"
        >
          Guardar
        </button>
      </div>
      <div class="mobile-preview-body">
        <div class="email-preview-frame">
          <div class="email-preview">
            <div class="email-logo">SIETE</div>
            <div class="email-card">
              <div class="email-header">
                <div class="email-icon">
                  <i class="material-icons">person</i>
                </div>
                <h1 v-if="hasSubjectContent" class="email-title">
                  {{ previewSubject }}
                </h1>
                <div v-else class="email-subject-placeholder">
                  <i class="material-icons">info</i>
                  <span>Asunto requerido</span>
                </div>
              </div>
              <div class="email-body">
                <div class="email-tag">{{ moduleTag }}</div>
                <div class="email-greeting">{{ previewGreeting }}</div>
                <div
                  v-if="hasBodyContent"
                  class="email-content"
                  v-html="previewBody"
                ></div>
                <div v-else class="email-content-placeholder">
                  <p>
                    <i class="material-icons">info</i>
                    Contenido requerido (mín. 50 caracteres)
                  </p>
                </div>
                <div
                  v-if="template.cta.enabled && template.cta.text"
                  class="email-cta"
                >
                  <a href="#">{{ template.cta.text }}</a>
                </div>
                <div v-if="template.footerNote" class="email-footer-note">
                  {{ template.footerNote }}
                </div>
              </div>
              <div class="email-footer">
                Siete S.A.S. · {{ empresaNombre }}<br />
                Si tienes dificultades, comunícate con
                <a href="mailto:elena@sietegho.com">elena@sietegho.com</a>
              </div>
            </div>
            <div class="email-social-links">
              <a href="https://www.sietegho.com" target="_blank" class="social-btn web">
                <img :src="require('@/assets/icons/earth.png')" alt="Web" />
              </a>
              <a href="https://www.linkedin.com/company/sietegho" target="_blank" class="social-btn linkedin">
                <img :src="require('@/assets/icons/linkedin.png')" alt="LinkedIn" />
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
    </transition>
  </div>
</template>

<script>
import { mapState } from "vuex";
import {
  getEmailTypesForModule,
  getModuleConfig,
  serializeContent,
  deserializeContent,
  replacePlaceholdersForPreview,
  getDefaultTemplate,
  isFixedCtaType,
  PLACEHOLDERS,
} from "../helpers";
import {
  fetchTemplateByType,
  saveTemplate as apiSaveTemplate,
  deleteTemplate,
} from "../api";

export default {
  name: "EmailTemplateEditor",
  props: {
    module: {
      type: String,
      required: true,
      validator: (v) => ["liderazgo", "clima", "cultura", "usuarios"].includes(v),
    },
    clienteId: {
      type: String,
      default: null,
    },
    procesoId: {
      type: String,
      default: null,
      required: false,
    },
  },
  data() {
    return {
      selectedEmailType: null,
      template: {
        subject: "",
        greeting: "Hola {NOMBRE}",
        body: "",
        cta: {
          enabled: true,
          text: "",
          urlType: "auto",
          customUrl: "",
        },
        footerNote: "",
      },
      templateId: null,
      isDefault: true,
      isGlobal: false,
      saving: false,
      previewSubject: "",
      previewGreeting: "",
      previewBody: "",
      showLinkTooltip: false,
      linkUrl: "",
      savedSelection: null,
      tooltipPosition: { top: 0, left: 0 },
      showLinkPreview: false,
      activeLinkElement: null,
      activeLinkUrl: "",
      greetingHasNombreChip: false,
      showMobilePreview: false,
      isMobileView: false,
    };
  },
  computed: {
    ...mapState(["empresa", "tokenUser"]),
    emailTypes() {
      return getEmailTypesForModule(this.module);
    },
    moduleConfig() {
      return getModuleConfig(this.module);
    },
    isFixedCta() {
      return isFixedCtaType(this.selectedEmailType);
    },
    moduleTag() {
      return (this.moduleConfig && this.moduleConfig.moduleTag) || "";
    },
    empresaNombre() {
      return (this.empresa && this.empresa.nombre) || "Empresa";
    },
    previewUserName() {
      return (this.tokenUser && this.tokenUser.name) || "Usuario";
    },
    hasBodyContent() {
      return this.template.body && this.template.body.trim().length > 0;
    },
    hasSubjectContent() {
      return this.template.subject && this.template.subject.trim().length > 0;
    },
    truncatedLinkUrl() {
      if (!this.activeLinkUrl) return "";
      if (this.activeLinkUrl.length > 30) {
        return this.activeLinkUrl.substring(0, 30) + "...";
      }
      return this.activeLinkUrl;
    },
  },
  watch: {
    module: {
      immediate: true,
      handler() {
        if (this.emailTypes.length > 0) {
          this.selectEmailType(this.emailTypes[0].key);
        }
      },
    },
  },
  mounted: function() {
    this.checkMobileView();
    window.addEventListener("resize", this.checkMobileView);
  },
  beforeDestroy: function() {
    window.removeEventListener("resize", this.checkMobileView);
  },
  methods: {
    checkMobileView: function() {
      this.isMobileView = window.innerWidth < 768;
      if (!this.isMobileView) {
        this.showMobilePreview = false;
      }
    },
    async selectEmailType(emailType) {
      this.selectedEmailType = emailType;
      await this.loadTemplate();
    },

    async loadTemplate() {
      try {
        const result = await fetchTemplateByType(
          this.module,
          this.selectedEmailType,
          this.procesoId,
          this.clienteId,
        );

        this.isDefault = result.isDefault;
        this.isGlobal = result.isGlobal || false;
        this.templateId = result.template._id || null;

        const cta = result.template.cta || {};
        this.template = {
          subject: result.template.subject || "",
          greeting: result.template.greeting || "Hola {NOMBRE}",
          body: result.template.body || "",
          cta: {
            enabled: cta.enabled !== undefined ? cta.enabled : true,
            text: cta.text || "",
            urlType: cta.urlType || "auto",
            customUrl: cta.customUrl || "",
          },
          footerNote: result.template.footerNote || "",
        };
        this.normalizeFixedCta();

        this.$nextTick(() => {
          if (this.$refs.editor) {
            this.$refs.editor.innerHTML = deserializeContent(
              this.template.body,
            );
          }
          this.loadGreetingEditor();
          this.updatePreview();
        });
      } catch (error) {
        console.error("Error loading template:", error);
        this.loadDefaultTemplate();
      }
    },

    loadDefaultTemplate() {
      const defaultTemplate = getDefaultTemplate(this.selectedEmailType);
      if (defaultTemplate) {
        this.isDefault = true;
        this.templateId = null;
        this.template = {
          subject: defaultTemplate.subject,
          greeting: defaultTemplate.greeting,
          body: defaultTemplate.body,
          cta: { ...defaultTemplate.cta },
          footerNote: defaultTemplate.footerNote,
        };
        this.normalizeFixedCta();

        this.$nextTick(() => {
          if (this.$refs.editor) {
            this.$refs.editor.innerHTML = deserializeContent(
              this.template.body,
            );
          }
          this.loadGreetingEditor();
          this.updatePreview();
        });
      }
    },

    onEditorInput() {
      this.template.body = serializeContent(this.$refs.editor.innerHTML);
      this.updatePreview();
    },

    onPaste(e) {
      e.preventDefault();
      const text = e.clipboardData.getData("text/plain");
      const html = text
        .replace(/\r\n/g, "<br>")
        .replace(/\n/g, "<br>")
        .replace(/\r/g, "<br>");
      document.execCommand("insertHTML", false, html);
    },

    // Greeting editor methods
    onGreetingInput() {
      this.updateGreetingFromEditor();
      this.updateGreetingHasChip();
      this.updatePreview();
    },

    onGreetingKeydown(e) {
      if (e.key !== "Backspace" && e.key !== "Delete") return;

      const editor = this.$refs.greetingEditor;
      if (!editor) return;

      const chip = editor.querySelector(".placeholder-chip.nombre");
      if (!chip) return;

      const selection = window.getSelection();
      if (selection.rangeCount === 0) return;

      const range = selection.getRangeAt(0);

      // Check if chip is selected or cursor is adjacent
      if (selection.containsNode(chip, true)) {
        e.preventDefault();
        chip.remove();
        this.updateGreetingFromEditor();
        this.updateGreetingHasChip();
        this.updatePreview();
        return;
      }

      // Backspace: check if cursor is right after chip
      if (e.key === "Backspace" && range.startOffset === 0) {
        const node = range.startContainer;
        let prev = node.previousSibling;
        if (!prev && node.parentNode === editor) {
          prev = null;
          const children = editor.childNodes;
          for (let i = 0; i < children.length; i++) {
            if (children[i] === node) break;
            prev = children[i];
          }
        }
        if (
          prev &&
          prev.classList &&
          prev.classList.contains("placeholder-chip")
        ) {
          e.preventDefault();
          prev.remove();
          this.updateGreetingFromEditor();
          this.updateGreetingHasChip();
          this.updatePreview();
        }
      }
    },

    insertGreetingChip() {
      if (this.greetingHasNombreChip) return;

      const editor = this.$refs.greetingEditor;
      if (!editor) return;

      const chip = document.createElement("span");
      chip.className = "placeholder-chip nombre";
      chip.contentEditable = "false";
      chip.innerHTML =
        '<i class="material-icons">account_circle</i><span>Nombre</span>';

      const selection = window.getSelection();

      if (selection.rangeCount > 0 && editor.contains(selection.anchorNode)) {
        const range = selection.getRangeAt(0);
        range.deleteContents();
        range.insertNode(chip);

        // Add space after and move cursor
        const space = document.createTextNode(" ");
        if (chip.nextSibling) {
          chip.parentNode.insertBefore(space, chip.nextSibling);
        } else {
          chip.parentNode.appendChild(space);
        }
        range.setStartAfter(space);
        range.collapse(true);
        selection.removeAllRanges();
        selection.addRange(range);
      } else {
        editor.appendChild(chip);
        const spaceNode = document.createTextNode(" ");
        editor.appendChild(spaceNode);
      }

      editor.focus();
      this.updateGreetingFromEditor();
      this.updateGreetingHasChip();
      this.updatePreview();
    },

    updateGreetingHasChip() {
      const editor = this.$refs.greetingEditor;
      this.greetingHasNombreChip =
        editor && editor.querySelector(".placeholder-chip.nombre") !== null;
    },

    updateGreetingFromEditor() {
      const editor = this.$refs.greetingEditor;
      if (!editor) return;

      const html = editor.innerHTML;
      // Replace chip HTML with {NOMBRE}
      const text = html
        .replace(
          /<span[^>]*class="[^"]*placeholder-chip[^"]*nombre[^"]*"[^>]*>[\s\S]*?<\/span>/gi,
          "{NOMBRE}",
        )
        .replace(/<[^>]+>/g, "") // Remove remaining HTML tags
        .replace(/&nbsp;/g, " ");

      this.template.greeting = text;
    },

    loadGreetingEditor() {
      const editor = this.$refs.greetingEditor;
      if (!editor) return;

      const greeting = this.template.greeting || "Hola {NOMBRE}";

      // Replace {NOMBRE} with chip HTML
      const html = greeting.replace(
        /\{NOMBRE\}/g,
        '<span class="placeholder-chip nombre" contenteditable="false"><i class="material-icons">account_circle</i><span>Nombre</span></span>',
      );

      editor.innerHTML = html;
      this.updateGreetingHasChip();
    },

    updatePreview() {
      this.previewSubject = replacePlaceholdersForPreview(
        this.template.subject,
        this.empresaNombre,
        this.previewUserName,
      );
      this.previewGreeting = replacePlaceholdersForPreview(
        this.template.greeting,
        this.empresaNombre,
        this.previewUserName,
      );

      var bodyText = replacePlaceholdersForPreview(
        this.template.body,
        this.empresaNombre,
        this.previewUserName,
      );
      var paragraphs = bodyText.split(/\n/);
      var bodyHtml = "";

      for (var i = 0; i < paragraphs.length; i++) {
        var p = paragraphs[i];
        if (p.trim()) {
          bodyHtml += "<p>" + p + "</p>";
        } else if (i > 0 && i < paragraphs.length - 1) {
          bodyHtml += "<br>";
        }
      }

      this.previewBody = bodyHtml;
    },

    formatText(command) {
      document.execCommand(command, false, null);
      this.$refs.editor.focus();
      this.onEditorInput();
    },

    insertLink() {
      var selection = window.getSelection();
      if (selection.rangeCount === 0) return;

      var range = selection.getRangeAt(0);
      if (!this.$refs.editor.contains(range.commonAncestorContainer)) {
        this.$refs.editor.focus();
        return;
      }

      this.savedSelection = range.cloneRange();

      var rect = range.getBoundingClientRect();
      var editorRect = this.$refs.editor.parentElement.getBoundingClientRect();

      this.tooltipPosition = {
        top: rect.bottom - editorRect.top + 8,
        left: rect.left - editorRect.left + rect.width / 2 - 120,
      };

      if (this.tooltipPosition.left < 0) this.tooltipPosition.left = 0;

      this.linkUrl = "";
      this.showLinkTooltip = true;

      this.$nextTick(
        function() {
          if (this.$refs.linkInput) {
            this.$refs.linkInput.focus();
          }
        }.bind(this),
      );
    },

    applyLink() {
      if (!this.linkUrl || !this.savedSelection) {
        this.cancelLink();
        return;
      }

      var url = this.linkUrl;
      if (!/^https?:\/\//i.test(url)) {
        url = "https://" + url;
      }

      var selection = window.getSelection();
      selection.removeAllRanges();
      selection.addRange(this.savedSelection);

      document.execCommand("createLink", false, url);
      this.onEditorInput();
      this.cancelLink();
    },

    cancelLink() {
      this.showLinkTooltip = false;
      this.linkUrl = "";
      this.savedSelection = null;
      this.activeLinkElement = null;
      this.$refs.editor.focus();
    },

    closeLinkPreview() {
      this.showLinkPreview = false;
      this.activeLinkElement = null;
      this.activeLinkUrl = "";
    },

    onEditorClick(e) {
      var target = e.target;
      if (target.tagName === "A") {
        e.preventDefault();
        this.activeLinkElement = target;
        this.activeLinkUrl = target.href;

        var rect = target.getBoundingClientRect();
        var editorRect = this.$refs.editor.parentElement.getBoundingClientRect();

        this.tooltipPosition = {
          top: rect.bottom - editorRect.top + 8,
          left: rect.left - editorRect.left + rect.width / 2 - 100,
        };

        if (this.tooltipPosition.left < 0) this.tooltipPosition.left = 0;

        this.showLinkPreview = true;
      } else {
        this.closeLinkPreview();
      }
    },

    editActiveLink() {
      if (!this.activeLinkElement) return;

      var range = document.createRange();
      range.selectNodeContents(this.activeLinkElement);
      this.savedSelection = range;

      this.linkUrl = this.activeLinkUrl;
      this.showLinkPreview = false;
      this.showLinkTooltip = true;

      this.$nextTick(
        function() {
          if (this.$refs.linkInput) {
            this.$refs.linkInput.focus();
            this.$refs.linkInput.select();
          }
        }.bind(this),
      );
    },

    removeActiveLink() {
      if (!this.activeLinkElement) return;

      var text = this.activeLinkElement.textContent;
      var textNode = document.createTextNode(text);
      this.activeLinkElement.parentNode.replaceChild(
        textNode,
        this.activeLinkElement,
      );

      this.closeLinkPreview();
      this.onEditorInput();
    },

    insertPlaceholder(type) {
      const config = PLACEHOLDERS.find((p) => p.key === type);
      if (!config) return;

      const chip = document.createElement("span");
      chip.className = `placeholder-chip ${type}`;
      chip.contentEditable = "false";
      chip.textContent = config.label;

      const selection = window.getSelection();
      if (selection.rangeCount > 0) {
        const range = selection.getRangeAt(0);
        if (this.$refs.editor.contains(range.commonAncestorContainer)) {
          range.insertNode(chip);
          range.setStartAfter(chip);
          range.collapse(true);
          selection.removeAllRanges();
          selection.addRange(range);
        } else {
          this.$refs.editor.appendChild(chip);
        }
      } else {
        this.$refs.editor.appendChild(chip);
      }

      this.$refs.editor.focus();
      this.onEditorInput();
    },

    normalizeFixedCta() {
      if (!this.isFixedCta) return;
      this.template.cta.enabled = true;
      this.template.cta.urlType = "auto";
      this.template.cta.customUrl = "";
    },

    toggleCta() {
      this.template.cta.enabled = !this.template.cta.enabled;
      this.updatePreview();
    },

    async saveTemplate() {
      var subject = (this.template.subject || "").trim();
      var body = (this.template.body || "").trim();

      if (!subject) {
        this.$swal({
          text: "El asunto del correo es requerido.",
          icon: "warning",
          showConfirmButton: false,
          timer: 2000,
        });
        return;
      }

      if (!body || body.length < 50) {
        this.$swal({
          text: "El contenido del correo debe tener al menos 50 caracteres.",
          icon: "warning",
          showConfirmButton: false,
          timer: 2000,
        });
        return;
      }

      if (this.isFixedCta && !(this.template.cta.text || "").trim()) {
        this.$swal({
          text: "El texto del botón es requerido.",
          icon: "warning",
          showConfirmButton: false,
          timer: 2000,
        });
        return;
      }

      this.saving = true;
      try {
        const cta = this.isFixedCta
          ? {
              enabled: true,
              text: this.template.cta.text,
              urlType: "auto",
              customUrl: "",
            }
          : this.template.cta;
        const data = {
          module: this.module,
          emailType: this.selectedEmailType,
          cliente: this.clienteId,
          proceso: this.procesoId,
          subject: this.template.subject,
          greeting: this.template.greeting,
          body: this.template.body,
          cta,
          footerNote: this.template.footerNote,
        };

        const result = await apiSaveTemplate(data);
        this.templateId = result._id;
        this.isDefault = false;

        this.$swal({
          text: "Plantilla guardada correctamente.",
          icon: "success",
          showConfirmButton: false,
          timer: 1700,
        });
      } catch (error) {
        console.error("Error saving template:", error);
        this.$swal({
          text: "Error al guardar la plantilla.",
          icon: "error",
          showConfirmButton: false,
          timer: 1700,
        });
      } finally {
        this.saving = false;
      }
    },

    async resetToDefault() {
      var confirmText = this.procesoId
        ? "Se eliminará la personalización de este proceso y se usará la plantilla del cliente o la predeterminada."
        : "Se eliminará la personalización y se usará la plantilla predeterminada.";

      const confirm = await this.$swal({
        title: "¿Restaurar plantilla?",
        text: confirmText,
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Sí, restaurar",
        cancelButtonText: "Cancelar",
        cancelButtonColor: "#de5b5b",
      });

      if (confirm.isConfirmed) {
        try {
          if (this.templateId) {
            await deleteTemplate(this.templateId);
          }
          await this.loadTemplate();
          this.$swal({
            text: "Plantilla restaurada correctamente.",
            icon: "success",
            showConfirmButton: false,
            timer: 1700,
          });
        } catch (error) {
          console.error("Error resetting template:", error);
        }
      }
    },

    async saveAndClosePreview() {
      await this.saveTemplate();
      this.showMobilePreview = false;
    },
  },
};
</script>

<style lang="scss" scoped>
.email-template-editor {
  width: 100%;
}

.email-type-tabs {
  display: flex;
  gap: 4px;
  background: #f5f5f5;
  padding: 4px;
  border-radius: 6px;
  margin-bottom: 20px;
}

.email-type-tab {
  flex: 1;
  padding: 10px 16px;
  border: none;
  background: transparent;
  color: #666;
  font-size: 13px;
  cursor: pointer;
  border-radius: 4px;
  transition: all 0.15s ease;
  text-align: center;

  &:hover {
    color: #333;
    background: #fff;
  }

  &.active {
    background: #363636;
    color: #fff;
    font-weight: 500;
  }
}

.editor-layout {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;

  @media (max-width: 1100px) {
    grid-template-columns: 1fr;
  }
}

.editor-panel,
.preview-panel {
  background: #fff;
  border: 1px solid #e5e5e5;
  border-radius: 8px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.panel-header {
  padding: 14px 18px;
  border-bottom: 1px solid #e5e5e5;
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-shrink: 0;
}

.panel-title {
  font-size: 14px;
  font-weight: 600;
  color: #333;
}

.status-badge {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 10px;
  border-radius: 12px;
  font-size: 11px;
  font-weight: 500;

  .material-icons {
    font-size: 14px;
  }

  &.custom {
    background: rgba(5, 150, 105, 0.1);
    color: #059669;
  }

  &.default {
    background: #f5f5f5;
    color: #999;
  }

  &.global {
    background: #f3e8ff;
    color: #522687;
    border: 1px solid #d8b4fe;
  }
}

.panel-body {
  padding: 18px;
  flex: 1;
}

.form-group {
  margin-bottom: 18px;
}

.form-label {
  display: block;
  font-size: 12px;
  font-weight: 500;
  color: #666;
  margin-bottom: 6px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

// Greeting editor styles
.greeting-editor-container {
  border: 1px solid #dbdbdb;
  border-radius: 4px;
  transition: border-color 0.15s ease;

  &:focus-within {
    border-color: #522687;
  }
}

.greeting-editor-row {
  display: flex;
  align-items: center;
}

.greeting-editor {
  flex: 1;
  padding: 10px 12px;
  min-height: 42px;
  font-size: 14px;
  color: #333;
  outline: none;
  cursor: text;

  &:empty::before {
    content: attr(data-placeholder);
    color: #999;
    pointer-events: none;
  }

  .placeholder-chip {
    display: inline-flex;
    align-items: center;
    padding: 3px 10px;
    margin: 0 4px;
    border-radius: 12px;
    font-size: 12px;
    font-weight: 500;
    vertical-align: middle;
    background: rgba(59, 130, 246, 0.15);
    color: #3b82f6;
    line-height: 1;

    .material-icons {
      font-size: 14px;
      margin-right: 4px;
    }
  }
}

.greeting-insert-btn {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 6px 12px;
  margin: 6px;
  border: 1px dashed #dbdbdb;
  border-radius: 4px;
  background: white;
  color: #666;
  font-size: 12px;
  cursor: pointer;
  transition: all 0.15s ease;
  white-space: nowrap;

  .material-icons {
    font-size: 16px;
  }

  &:hover:not(:disabled) {
    border-color: #3b82f6;
    background: rgba(59, 130, 246, 0.08);
    color: #3b82f6;
  }

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
}

.rich-editor-container {
  position: relative;
  border: 1px solid #dbdbdb;
  border-radius: 4px;
  overflow: visible;
}

.rich-editor-toolbar {
  display: flex;
  gap: 4px;
  padding: 8px 10px;
  background: #f9f9f9;
  border-bottom: 1px solid #e5e5e5;
  flex-wrap: wrap;
  align-items: center;
}

.toolbar-group {
  display: flex;
  gap: 2px;
  align-items: center;
}

.toolbar-divider {
  width: 1px;
  height: 24px;
  background: #e5e5e5;
  margin: 0 8px;
}

.toolbar-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border: none;
  background: transparent;
  color: #666;
  cursor: pointer;
  border-radius: 4px;
  transition: all 0.15s ease;

  &:hover {
    color: #5e2ca5;
    background: #f0ebf9;
  }

  .material-icons {
    font-size: 18px;
  }
}

.toolbar-insert {
  display: flex;
  align-items: center;
  padding: 4px;
  border: none;
  background: transparent;
  cursor: pointer;
  border-radius: 4px;
  transition: all 0.15s ease;

  &:hover {
    background: #f0ebf9;
  }
}

.chip-preview {
  display: inline-flex;
  align-items: center;
  padding: 2px 8px;
  border-radius: 10px;
  font-size: 11px;
  font-weight: 500;

  .material-icons {
    font-size: 14px;
    margin-right: 4px;
  }

  &.nombre {
    background: rgba(59, 130, 246, 0.15);
    color: #3b82f6;
  }

  &.empresa {
    background: rgba(16, 185, 129, 0.15);
    color: #10b981;
  }
}

.rich-editor-content {
  min-height: 160px;
  padding: 14px 16px;
  font-size: 14px;
  line-height: 1.7;
  color: #333;
  outline: none;

  &:empty::before {
    content: attr(data-placeholder);
    color: #999;
  }
}

.link-tooltip {
  position: absolute;
  z-index: 100;
  display: flex;
  align-items: center;
  gap: 6px;
  background: #363636;
  padding: 8px 10px;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.link-tooltip-arrow {
  position: absolute;
  top: -6px;
  left: 50%;
  transform: translateX(-50%);
  width: 0;
  height: 0;
  border-left: 6px solid transparent;
  border-right: 6px solid transparent;
  border-bottom: 6px solid #363636;
}

.link-tooltip-input {
  width: 180px;
  padding: 6px 10px;
  border: none;
  border-radius: 4px;
  font-size: 13px;
  background: #fff;
  color: #333;

  &:focus {
    outline: none;
  }

  &::placeholder {
    color: #999;
  }
}

.link-tooltip-actions {
  display: flex;
  gap: 4px;
}

.link-tooltip-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  transition: background 0.15s ease;

  .material-icons {
    font-size: 18px;
  }

  &.cancel {
    background: transparent;
    color: #999;

    &:hover {
      background: rgba(255, 255, 255, 0.1);
      color: #fff;
    }
  }

  &.apply {
    background: #5e2ca5;
    color: #fff;

    &:hover {
      background: #4a2384;
    }
  }

  &.edit {
    background: transparent;
    color: #ccc;

    &:hover {
      background: rgba(255, 255, 255, 0.1);
      color: #fff;
    }
  }

  &.remove {
    background: transparent;
    color: #ccc;

    &:hover {
      background: rgba(239, 68, 68, 0.2);
      color: #ef4444;
    }
  }
}

.link-preview-popover {
  position: absolute;
  z-index: 100;
  display: flex;
  align-items: center;
  gap: 8px;
  background: #363636;
  padding: 8px 12px;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.link-preview-url {
  color: #a5b4fc;
  font-size: 13px;
  text-decoration: none;
  max-width: 150px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  &:hover {
    color: #c7d2fe;
    text-decoration: underline;
  }
}

.link-preview-actions {
  display: flex;
  gap: 2px;
  border-left: 1px solid #555;
  padding-left: 8px;
  margin-left: 4px;
}

.toggle-section {
  background: #f9f9f9;
  border-radius: 6px;
  margin-bottom: 18px;
  overflow: hidden;
}

.toggle-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 16px;
  cursor: pointer;
}

.toggle-header-left {
  display: flex;
  align-items: center;
  gap: 10px;
  color: #333;
  font-size: 13px;
  font-weight: 500;

  .material-icons {
    font-size: 18px;
    color: #666;
  }
}

.toggle-switch {
  position: relative;
  width: 40px;
  height: 22px;
  background: #ccc;
  border-radius: 11px;
  cursor: pointer;
  transition: background 0.2s ease;

  &.active {
    background: #5e2ca5;
  }

  &::after {
    content: "";
    position: absolute;
    top: 2px;
    left: 2px;
    width: 18px;
    height: 18px;
    background: #fff;
    border-radius: 50%;
    transition: transform 0.2s ease;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
  }

  &.active::after {
    transform: translateX(18px);
  }
}

.toggle-content {
  padding: 0 16px 16px;
}

.radio-group {
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-top: 8px;
}

.radio-item {
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;

  input[type="radio"] {
    display: none;
  }
}

.radio-circle {
  width: 18px;
  height: 18px;
  border: 2px solid #ccc;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: border-color 0.15s ease;

  .radio-item.selected & {
    border-color: #5e2ca5;
  }

  &::after {
    content: "";
    width: 8px;
    height: 8px;
    background: #5e2ca5;
    border-radius: 50%;
    opacity: 0;
    transition: opacity 0.15s ease;

    .radio-item.selected & {
      opacity: 1;
    }
  }
}

.radio-label {
  font-size: 13px;
  color: #333;
}

.button-group {
  display: flex;
  gap: 10px;
  margin-top: 20px;
  padding-top: 18px;
  border-top: 1px solid #e5e5e5;
}

// Preview styles
.preview-body {
  padding: 0;
  flex: 1;
  display: flex;
  flex-direction: column;
}

.email-preview-frame {
  background: #e8e8e8;
  padding: 20px;
  flex: 1;
  min-height: 480px;
}

.email-preview {
  max-width: 440px;
  margin: 0 auto;
  font-family: Arial, Helvetica, sans-serif;
}

.email-logo {
  text-align: center;
  padding-bottom: 16px;
  font-size: 20px;
  font-weight: bold;
  color: #5e2ca5;
}

.email-card {
  background: #fff;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}

.email-header {
  background: #f0ebf9;
  padding: 24px;
  text-align: center;
}

.email-icon {
  width: 40px;
  height: 40px;
  background: #fff;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 12px;

  .material-icons {
    color: #5e2ca5;
    font-size: 20px;
  }
}

.email-title {
  font-size: 16px;
  font-weight: bold;
  color: #363636;
  margin: 0;
}

.email-body {
  padding: 24px;
}

.email-tag {
  display: inline-block;
  background: #363636;
  color: #fff;
  padding: 3px 10px;
  border-radius: 16px;
  font-size: 9px;
  font-weight: bold;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  margin-bottom: 12px;
}

.email-greeting {
  font-size: 14px;
  font-weight: bold;
  color: #363636;
  margin-bottom: 10px;
}

.email-content {
  font-size: 13px;
  line-height: 1.7;
  color: #666;
  margin-bottom: 16px;

  ::v-deep p {
    margin-bottom: 10px;
  }
}

.email-cta {
  text-align: center;
  margin: 20px 0;

  a {
    display: inline-block;
    background: #5e2ca5;
    color: #fff;
    padding: 10px 28px;
    border-radius: 8px;
    text-decoration: none;
    font-weight: bold;
    font-size: 13px;
  }
}

.email-footer-note {
  text-align: center;
  font-size: 10px;
  color: #999;
}

.email-footer {
  padding: 16px 24px;
  text-align: center;
  font-size: 10px;
  color: #999;
  border-top: 1px solid #eee;

  a {
    color: #5e2ca5;
    text-decoration: none;
  }
}

.email-social-links {
  display: flex;
  justify-content: center;
  gap: 10px;
  padding-top: 16px;
}

.social-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 6px;

  img {
    width: 18px;
    height: 18px;
  }

  &.web {
    background-color: #5e2ca5;
  }

  &.linkedin {
    background-color: #0A66C2;
  }
}
</style>

<style>
.placeholder-chip {
  display: inline-flex !important;
  align-items: center !important;
  padding: 3px 10px !important;
  margin: 0 4px !important;
  border-radius: 14px !important;
  font-size: 12px !important;
  font-weight: 600 !important;
  vertical-align: middle !important;
  line-height: 1 !important;
  user-select: all;
  cursor: default;
  border: 1px solid !important;
}

.placeholder-chip .material-icons {
  font-size: 14px !important;
  margin-right: 4px !important;
}

.placeholder-chip.nombre {
  background: rgba(59, 130, 246, 0.12) !important;
  color: #2563eb !important;
  border-color: rgba(59, 130, 246, 0.3) !important;
}

.placeholder-chip.empresa {
  background: rgba(16, 185, 129, 0.12) !important;
  color: #059669 !important;
  border-color: rgba(16, 185, 129, 0.3) !important;
}

.email-subject-placeholder {
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
  gap: 6px !important;
  background: rgba(220, 107, 107, 0.1) !important;
  padding: 8px 14px !important;
  border-radius: 6px !important;
  border: 1px dashed #f0d4d4 !important;
  width: fit-content !important;
  margin: 0 auto !important;
}

.email-subject-placeholder span {
  color: #dc6b6b !important;
  font-size: 14px !important;
  font-weight: 500 !important;
  font-style: italic !important;
}

.email-subject-placeholder i {
  font-size: 16px !important;
  color: #dc6b6b !important;
}

.email-content-placeholder {
  background: #fdf4f4 !important;
  border: 1px dashed #f0d4d4 !important;
  border-radius: 8px !important;
  padding: 16px !important;
  text-align: center !important;
  margin-bottom: 16px !important;
}

.email-content-placeholder p {
  color: #dc6b6b !important;
  font-weight: 500 !important;
  font-size: 13px !important;
  margin: 0 !important;
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
  gap: 6px !important;
}

.email-content-placeholder i {
  font-size: 16px !important;
  color: #dc6b6b !important;
}

/* Botón flotante preview móvil */
.preview-fab {
  display: none;
  position: fixed;
  bottom: 24px;
  right: 24px;
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: #522687;
  color: white;
  border: none;
  box-shadow: 0 4px 12px rgba(82, 38, 135, 0.4);
  cursor: pointer;
  align-items: center;
  justify-content: center;
  z-index: 100;
  transition: transform 0.15s ease, box-shadow 0.15s ease;

  &:hover {
    transform: scale(1.05);
    box-shadow: 0 6px 16px rgba(82, 38, 135, 0.5);
  }

  &:active {
    transform: scale(0.95);
  }

  .material-icons {
    font-size: 22px;
  }
}

/* Overlay de preview móvil */
.mobile-preview-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: #f5f5f5;
  z-index: 1000;
  display: flex;
  flex-direction: column;
}

/* Transición de expansión desde el FAB */
.preview-expand-enter-active {
  animation: expandFromFab 0.4s cubic-bezier(0.4, 0, 0.2, 1) forwards;
}

.preview-expand-leave-active {
  animation: expandFromFab 0.3s cubic-bezier(0.4, 0, 0.2, 1) reverse forwards;
}

@keyframes expandFromFab {
  0% {
    clip-path: circle(24px at calc(100% - 48px) calc(100% - 48px));
  }
  100% {
    clip-path: circle(150% at calc(100% - 48px) calc(100% - 48px));
  }
}

.mobile-preview-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  background: white;
  border-bottom: 1px solid #e5e5e5;
  position: sticky;
  top: 0;
  z-index: 10;
}

.back-btn {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 14px;
  background: transparent;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 14px;
  color: #374151;
  cursor: pointer;

  .material-icons {
    font-size: 20px;
  }
}

.save-btn-mobile {
  padding: 8px 20px;
  background: #522687;
  color: white;
  border: none;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
}

.mobile-preview-body {
  flex: 1;
  overflow-y: auto;
  padding: 16px;
}

/* Responsive móvil */
@media (max-width: 768px) {
  /* Mostrar botón flotante en móvil */
  .preview-fab {
    display: flex;
  }

  /* Ocultar preview panel en móvil */
  .preview-panel {
    display: none !important;
  }

  /* Tabs con scroll horizontal */
  .email-type-tabs {
    overflow-x: auto;
    flex-wrap: nowrap;
    -webkit-overflow-scrolling: touch;
    padding-bottom: 4px;

    &::-webkit-scrollbar {
      display: none;
    }
  }

  .email-type-tab {
    white-space: nowrap;
    flex-shrink: 0;
  }

  .editor-layout {
    grid-template-columns: 1fr !important;
  }

  .panel-header {
    flex-wrap: wrap;
    gap: 8px;
  }

  .toggle-section .toggle-header {
    padding: 12px;
  }

  .toggle-section .toggle-content {
    padding: 0 12px 12px;
  }

  .button-group {
    flex-direction: column;

    .button {
      width: 100%;
    }
  }
}
</style>



// WEBPACK FOOTER //
// src/components/shared/emailEditor/components/EmailTemplateEditor.vue